import { type Driver, type DriveStep } from 'driver.js'
import type { TFunction } from 'i18next'
import type { RxCollection } from 'rxdb'

import { CARD_CREATION_ANIMATION_DURATION_SECONDS } from '~/boundedContext/list/components/animated-edit-card'
import type { Card } from '~/db/types'
import { useSettingsStore } from '~/stores/settings'

import { listTourSelectorsFactory } from './selectors'

const WAIT_FOR_CARD_SCROLLED_MS = 600

const toDriveElement = (getElement: () => Element | null | undefined) => () => {
  const element = getElement()
  if (!element) throw new Error('Onboarding tour target element not found')

  return element
}

export const createListTourSteps = ({
  cardsCollection,
  driverObj,
  onTourCompleted,
  t,
}: {
  cardsCollection: RxCollection<Card>
  driverObj: Driver
  onTourCompleted: () => void
  t: TFunction<['onboarding', 'translation']>
}) => {
  const { advanceTourProgress, completeTourProgress } = useSettingsStore.getState()
  const pronunciationAriaLabel = t('pronunciation text input', { ns: 'translation' })
  const exampleAriaLabel = t('Example text area input', { ns: 'translation' })
  const snoozeAriaLabel = t('Snooze card', { ns: 'translation' })
  const refreshAriaLabel = t('refresh example button', { ns: 'translation' })

  const persistStep = (stepIndex: number) => {
    advanceTourProgress('list', stepIndex)
  }

  const completeTour = () => {
    completeTourProgress('list')
    onTourCompleted()
  }

  const highlightStep = (step: DriveStep, stepIndex: number) => {
    persistStep(stepIndex)
    driverObj.highlight(step)
  }

  const cleanups = [] as (() => void)[]
  const cleanup = () => cleanups.forEach(cleanup => cleanup())

  const listTourSelectors = listTourSelectorsFactory(
    t('remove item button', { ns: 'translation' }),
    t('paste text into the field button', { ns: 'translation' }),
    t('accordion toggle button', { ns: 'translation' }),
  )
  const refreshExampleStep: DriveStep = {
    element: toDriveElement(listTourSelectors.getFieldInLastCard(refreshAriaLabel)),
    popover: {
      title: t('Refresh example'),
      description: t('Refresh example hint'),
      showButtons: ['next', 'close'],
      onNextClick: completeTour,
    },
  }

  const snoozeStep: DriveStep = {
    element: toDriveElement(listTourSelectors.getFieldInLastCard(snoozeAriaLabel)),
    popover: {
      title: t('Snooze'),
      description: t('Snooze card hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(refreshExampleStep, 5),
    },
  }

  const exampleStep: DriveStep = {
    element: toDriveElement(listTourSelectors.getFieldInLastCard(exampleAriaLabel)),
    popover: {
      title: t('Example sentence'),
      description: t('Example autofill hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(snoozeStep, 4),
    },
  }

  const pronunciationStep: DriveStep = {
    element: toDriveElement(listTourSelectors.getFieldInLastCard(pronunciationAriaLabel)),
    popover: {
      title: t('Pronunciation'),
      description: t('Pronunciation autofill hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(exampleStep, 3),
    },
  }

  const expandIconStep: DriveStep = {
    element: toDriveElement(listTourSelectors.getExpandIcon),
    popover: {
      title: t('Open card details'),
      description: t('Tap expand for details'),
      showButtons: ['close'],
      onPopoverRender: () => {
        const expandIcon = listTourSelectors.getExpandIcon()
        if (!expandIcon) return console.error({ message: 'Expand icon not found' })

        const expandClickHandler = () => {
          setTimeout(() => {
            highlightStep(pronunciationStep, 2)
          }, CARD_CREATION_ANIMATION_DURATION_SECONDS * 1000)

          expandIcon.removeEventListener('click', expandClickHandler!)
        }
        expandIcon.addEventListener('click', expandClickHandler)
        cleanups.push(() => expandIcon.removeEventListener('click', expandClickHandler))
      },
    },
  }

  const addMethodsStep: DriveStep = {
    element: toDriveElement(listTourSelectors.getAddMethodsRow),
    popover: {
      title: t('Add a word'),
      description: t('Add methods hint'),
      showButtons: ['close', 'next'],
      nextBtnText: t('Got it'),
      onNextClick: () => {
        driverObj.destroy()
        const subscription = cardsCollection.insert$.subscribe(() => {
          setTimeout(() => highlightStep(expandIconStep, 1), WAIT_FOR_CARD_SCROLLED_MS)
        })
        cleanups.push(() => subscription.unsubscribe())
      },
    },
  }

  const steps = [addMethodsStep, expandIconStep, pronunciationStep, exampleStep, snoozeStep, refreshExampleStep]

  return { cleanup, highlightStep, steps }
}
